import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import useGithubReadme from "../../../hooks/useGithubReadme";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function DocViewer({
  width = 650,
  height = 700,
  initialPosition,
  name,
  owner,
  onClose,
  src,
}) {
  const [position, setPosition] = useState(
    initialPosition ?? { x: 200, y: 150 }
  );

  const [isDragging, setIsDragging] = useState(false);

  // Local markdown state
  const [localMarkdown, setLocalMarkdown] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // GitHub README
  const {
    readme,
    loading: githubLoading,
    error: githubError,
  } = useGithubReadme(
    src ? null : owner,
    src ? null : name
  );

  const dragOffset = useRef({ x: 0, y: 0 });
  const windowRef = useRef(null);

  // Fetch local markdown file
  useEffect(() => {
    if (!src) return;

    async function fetchLocalMarkdown() {
      try {
        setLocalLoading(true);
        setLocalError(null);

        const response = await fetch(src);

        if (!response.ok) {
          throw new Error("Failed to load document");
        }

        const text = await response.text();

        setLocalMarkdown(text);
      } catch (error) {
        setLocalError(error.message);
      } finally {
        setLocalLoading(false);
      }
    }

    fetchLocalMarkdown();
  }, [src]);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;

    const rect = windowRef.current?.getBoundingClientRect();

    if (rect) {
      dragOffset.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    }

    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      setPosition({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Choose content source
  const markdown = src ? localMarkdown : readme;
  const loading = src ? localLoading : githubLoading;
  const error = src ? localError : githubError;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-[60]"
        style={{ backgroundColor: "rgba(0,0,0,0.07)" }}
        onClick={onClose}
      />

      {/* Window */}
      <motion.div
        ref={windowRef}
        className="fixed z-[70] overflow-hidden"
        initial={{ scale: 0.8, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{
          type: "spring",
          stiffness: 260,
          damping: 20,
        }}
        style={{
          width,
          height,
          left: position.x,
          top: position.y,
          borderRadius: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          cursor: isDragging ? "grabbing" : "default",
        }}
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header */}
        <div
          className="relative flex items-center bg-slate-100/80 backdrop-blur-sm px-4 pt-3 pb-2 cursor-grab"
          onMouseDown={handleMouseDown}
        >

          {/* macOS buttons */}
          <div className="flex items-center gap-2 z-10">

            <button
              onClick={onClose}
              className="w-3 h-3 rounded-full flex items-center justify-center group"
              style={{
                backgroundColor: "#ff6157",
                border: "0.5px solid #e24640",
              }}
            >
              <svg
                width="6"
                height="6"
                viewBox="0 0 6 6"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <path
                  d="M0.5 0.5L5.5 5.5M5.5 0.5L0.5 5.5"
                  stroke="#4c0002"
                  strokeWidth="1"
                />
              </svg>
            </button>

            <div className="w-3 h-3 rounded-full bg-[#ffbd2e] border border-[#dea123]" />
            <div className="w-3 h-3 rounded-full bg-[#28c940] border border-[#1bac2c]" />

          </div>

          {/* Title */}
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[13px] font-bold text-black/70 pointer-events-none font-applefont">
            {name || "Document"}
          </h1>
        </div>

        {/* Content */}
        <div className="w-full h-full bg-white overflow-y-auto">

          {loading && (
            <div className="flex h-full items-center justify-center">
              Loading...
            </div>
          )}

          {!loading && error && (
            <div className="flex h-full items-center justify-center text-red-500">
              {error}
            </div>
          )}

          {!loading && !error && (
            <article className="prose prose-sm max-w-none px-8 py-4 font-['Space_Mono']">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdown}
              </ReactMarkdown>
            </article>
          )}

        </div>

      </motion.div>
    </>
  );
}